import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EMPLOYEE_WORK_MAX_ROWS,
  EMPLOYEE_WORK_PART_MAX_LENGTH,
  EmployeeWorkSchema,
  WorkerSchema,
  type EmployeeWorkDto,
} from '@taqon/contracts';
import { z } from 'zod';
import { errorMessage, getData, putJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { SearchPicker } from '../finance/lookups';
import { PRODUCTS_SERVICES_PATH } from '../organization/ProductsServicesPage';

/**
 * What an employee is working on (management request, 23 October 2026): rows of a project, a
 * product or service and the part of it they handle, every field optional. Shown on the employee
 * page; HR replaces the list as a whole, under the employee's version.
 */

const workPath = (employeeId: string) =>
  `/api/v1/hr/employees/${encodeURIComponent(employeeId)}/work`;

/** A project or a product as the form knows it: an id and how it is named. */
const CodeNameSchema = z.object({ id: z.string(), code: z.string(), name: z.string() });
type CodeName = z.infer<typeof CodeNameSchema>;
const describe = (p: CodeName) => (p.name === '' ? p.code : `${p.code} — ${p.name}`);
const searchTerm = (term: string) =>
  term === '' ? '' : `&filter[search]=${encodeURIComponent(term)}`;

/** Active projects or products and services by code or name, searched on the server. */
function CodeNamePicker({
  kind,
  ...props
}: {
  kind: 'project' | 'product';
  label: string;
  value: CodeName | null;
  onChange: (value: CodeName | null) => void;
}) {
  return (
    <SearchPicker
      queryKey={kind === 'project' ? 'work-projects-active' : 'work-products-active'}
      schema={CodeNameSchema}
      path={(term) =>
        `${kind === 'project' ? '/api/v1/projects' : PRODUCTS_SERVICES_PATH}?pageSize=25&sort=code&filter[status]=active${searchTerm(term)}`
      }
      describe={describe}
      {...props}
    />
  );
}

export function EmployeeWorkSection({
  employeeId,
  canChange,
}: {
  employeeId: string;
  canChange: boolean;
}) {
  const work = useQuery({
    queryKey: ['hr', 'employee', employeeId, 'work'],
    retry: false,
    queryFn: () => getData(workPath(employeeId), EmployeeWorkSchema),
  });
  const [editing, setEditing] = useState(false);
  return (
    <Section
      title="Current work"
      actions={
        canChange && work.data !== undefined ? (
          <Button
            size="small"
            onClick={() => {
              setEditing(true);
            }}
          >
            Edit current work
          </Button>
        ) : undefined
      }
    >
      {work.isPending && <LoadingState label="Loading current work" />}
      {work.isError && <ErrorState message={work.error.message} />}
      {work.data?.rows.length === 0 && (
        <EmptyState title="Not stated" detail="The projects and products this employee works on." />
      )}
      {work.data !== undefined && work.data.rows.length > 0 && (
        <Table size="small" aria-label="Current work">
          <TableHead>
            <TableRow>
              <TableCell>Project</TableCell>
              <TableCell>Product / service</TableCell>
              <TableCell>Part they work on</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {work.data.rows.map((row, index) => (
              // The rows have no identity of their own; their order is their key.
              <TableRow key={index}>
                <TableCell>{row.projectLabel ?? '—'}</TableCell>
                <TableCell>{row.productServiceLabel ?? '—'}</TableCell>
                <TableCell sx={{ overflowWrap: 'anywhere' }}>{row.part ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {editing && work.data !== undefined && (
        <EditWorkDialog
          work={work.data}
          onClose={() => {
            setEditing(false);
          }}
        />
      )}
    </Section>
  );
}

interface DraftRow {
  readonly key: number;
  readonly project: CodeName | null;
  readonly product: CodeName | null;
  readonly part: string;
}

/** A saved reference as the picker shows it: its label as given, "CODE — Name". */
const saved = (id: string | null, label: string | null): CodeName | null =>
  id === null ? null : { id, code: label ?? id, name: '' };

function EditWorkDialog({ work, onClose }: { work: EmployeeWorkDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [nextKey, setNextKey] = useState(work.rows.length + 1);
  const [rows, setRows] = useState<DraftRow[]>(() =>
    work.rows.map((row, index) => ({
      key: index + 1,
      project: saved(row.projectId, row.projectLabel),
      product: saved(row.productServiceId, row.productServiceLabel),
      part: row.part ?? '',
    })),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const filled = (row: DraftRow) =>
    row.project !== null || row.product !== null || row.part.trim() !== '';
  const set = (key: number, change: Partial<DraftRow>) => {
    setRows((before) => before.map((row) => (row.key === key ? { ...row, ...change } : row)));
  };
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await putJson(
        workPath(work.employeeId),
        {
          rows: rows.filter(filled).map((row) => ({
            projectId: row.project?.id ?? null,
            productServiceId: row.product?.id ?? null,
            part: row.part.trim() === '' ? null : row.part.trim(),
          })),
        },
        EmployeeWorkSchema,
        session.data?.csrfToken ?? '',
        work.version,
      );
      // The employee moved to its next version: its page reloads it.
      await queryClient.invalidateQueries({ queryKey: ['hr', 'employee'] });
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? errorMessage(failure) : 'The change was not saved');
    } finally {
      setSaving(false);
    }
  };
  return (
    <FormDialog
      open
      title="Current work"
      submitLabel="Save"
      canSubmit={!saving}
      submitting={saving}
      error={error}
      onClose={onClose}
      onSubmit={() => {
        void save();
      }}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Each row: a project, the product or service, and the part of it this employee works on.
          Every field is optional; an empty row is not saved.
        </Typography>
        {rows.map((row, index) => (
          <Paper key={row.key} variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Row {index + 1}</Typography>
                <IconButton
                  size="small"
                  aria-label={`Remove row ${String(index + 1)}`}
                  onClick={() => {
                    setRows((before) => before.filter((r) => r.key !== row.key));
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
              <CodeNamePicker
                kind="project"
                label="Project"
                value={row.project}
                onChange={(project) => {
                  set(row.key, { project });
                }}
              />
              <CodeNamePicker
                kind="product"
                label="Product / service"
                value={row.product}
                onChange={(product) => {
                  set(row.key, { product });
                }}
              />
              <TextField
                label="Part they work on"
                value={row.part}
                onChange={(e) => {
                  set(row.key, { part: e.target.value });
                }}
                slotProps={{ htmlInput: { maxLength: EMPLOYEE_WORK_PART_MAX_LENGTH } }}
                helperText="For example: Appointments and payments module"
              />
            </Stack>
          </Paper>
        ))}
        <Box>
          <Button
            startIcon={<AddIcon />}
            disabled={rows.length >= EMPLOYEE_WORK_MAX_ROWS}
            onClick={() => {
              setRows((before) => [
                ...before,
                { key: nextKey, project: null, product: null, part: '' },
              ]);
              setNextKey((k) => k + 1);
            }}
          >
            Add a row
          </Button>
        </Box>
      </Stack>
    </FormDialog>
  );
}

/**
 * The employees working on a product or service (or a project), for its own page: shown to a
 * session that may read employees, each with the part they handle.
 */
export function WorkersSection({
  filter,
  title = 'People working on it',
}: {
  filter: { productServiceId: string } | { projectId: string };
  title?: string;
}) {
  const session = useSession();
  const can = hasCapability(session.data, 'hr.employee:view');
  const query =
    'productServiceId' in filter
      ? `productServiceId=${encodeURIComponent(filter.productServiceId)}`
      : `projectId=${encodeURIComponent(filter.projectId)}`;
  const workers = useQuery({
    queryKey: ['hr', 'work', query],
    enabled: can,
    retry: false,
    queryFn: () => getData(`/api/v1/hr/work?${query}`, z.array(WorkerSchema)),
  });
  if (!can) return null;
  return (
    <Section title={title}>
      {workers.isPending && <LoadingState label="Loading the people working on it" />}
      {workers.isError && <ErrorState message={workers.error.message} />}
      {workers.data?.length === 0 && (
        <EmptyState
          title="No one named yet"
          detail="An employee's page says which projects and products they work on."
        />
      )}
      {workers.data !== undefined && workers.data.length > 0 && (
        <Table size="small" aria-label={title}>
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>
                {'productServiceId' in filter ? 'Project' : 'Product / service'}
              </TableCell>
              <TableCell>Part they work on</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workers.data.map((worker, index) => (
              // An employee may work on several parts: the order is the key.
              <TableRow key={`${worker.employeeId}-${String(index)}`}>
                <TableCell>
                  <PersonName
                    name={`${worker.employeeCode} — ${worker.employeeName}`}
                    employeeId={worker.employeeId}
                    to={`/hr/employees/${worker.employeeId}`}
                  />
                </TableCell>
                <TableCell>
                  {('productServiceId' in filter
                    ? worker.projectLabel
                    : worker.productServiceLabel) ?? '—'}
                </TableCell>
                <TableCell sx={{ overflowWrap: 'anywhere' }}>{worker.part ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Section>
  );
}
